// src/com/trans_plan_launch/TransPlanLaunchItem.js
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, Chip, Grid2 } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person'; // 차량기사
import SystemSecurityUpdateGoodIcon from '@mui/icons-material/SystemSecurityUpdateGood'; // mobile
import StorefrontIcon from '@mui/icons-material/Storefront'; // 동적표시용.
import LocationOnIcon from '@mui/icons-material/LocationOn'; // 지도로 이동
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'; // save
import ClearIcon from '@mui/icons-material/Clear'; // delete
import SearchIcon from '@mui/icons-material/Search'; // search vehicle
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // set vehicle
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend'; // off truck
import MessageIcon from '@mui/icons-material/Message'; // message
import { useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppObject, AppStyle, AppWord, InputType, MapValue, TopCode } from '../../value/AppConst';
import {
    authTableTreeRepo, mapRequestRepo, vehInfoListOfDcRepo, waveListRepo
} from '../../value/AppModel';
import VzmSelector from '../../common/VzmSelector';
import { TransPlanLaunchDictionary } from './TransPlanLaunchDict';
import InputUtil from '../../value/InputUtil';
import Time24Picker from '../../common/Time24Picker';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';
import { NoDataBox } from '../../common/TopStyle';
import VzmTextField from '../../common/VzmTextField';
import VzmIntField from '../../common/VzmIntField';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import { SEND_TRANS_PLAN_GUIDE_SMS } from '../trans_plan/TransPlanGql';
import VehInfoSelectDialog from '../veh_info/VehInfoSelectDialog';


const InputMap = TransPlanLaunchDictionary.inputMap;

const LabelBox = styled(Box)({
    flexGrow:1, textAlign:'center', padding: '0.5rem', display: 'flex', alignItems:'center',
    justifyContent: 'center', 
});

const GrowCol = styled(Box)({
    flexGrow:1, display: 'flex', alignItems:'center',
});

const makeDefaultData = (baseData) => {
    const data = {};
    Object.keys(InputMap).forEach((key) => {
        const dict = InputMap[key];
        if((dict.type===InputType.number || dict.type===InputType.int) && dict.required) {
            data[key] = dict.default || 0;
        }
    });
    Object.assign(data, baseData);
    return data;
};

const getHhmm = (value) => {
    return value ? value.substring(11,16).replace(/\D/, '') : null
};

export const getVehicleObject = (vehInfoList, comId, vehPlates) => {
    if(vehPlates) {
        for(const veh of vehInfoList) {
            if(veh.vehPlates === vehPlates && veh.comId === comId) return veh;
        }
    }
    return null;
};

const EmptyVehInfoList = [{vehPlates:AppWord.ChooseKorean, driverName:'', driverMobile:''}];

/**
 * 수동 개별 배차등록.
 * 1. 배차 자체의 생성. (동적 선택 포함) - 과거 PR_TRANS_PLAN_NEW_REG (사실 new, edit 둘다 포함함)
 * 2. 차량을 선택하여 등록 - 과거 PR_TRANS_PLAN_EDIT_REG
 * 3. 차량에 메시지 전송.
 * 4. 배차 삭제 - 과거 PR_TRANS_PLAN_DEL
 * @param {*} param0 
 */
export default function TransPlanLaunchItem({
    hocha, // 호차
    baseData, // {dcCd, arriveId, storeId, planDate}
    item,
    vehTypeList,
    comInfoList,
    lastDockno,
    onRequestShowTsr,
    onRequestSave, // {param, isEdit}
    onRequestRemove,
    onRequestSetTruck, // , onRequestReload, onRequestPopupBindManager,
    onRequestOffTruck, // {planId}
    onRequestTruckOnMap,
    holdMutation
}) {
    // const menuTree = useReactiveVar(menuTreeRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [inputSetVehicle, setInputSetVehicle] = useState({...AppObject.emptyInputData});
    const [storesBound, setStoresBound] = useState(false);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const waveList = useReactiveVar(waveListRepo);
    const vehInfoListOfDcData = useReactiveVar(vehInfoListOfDcRepo);
    const [vehInfoList, setVehInfoList] = useState(EmptyVehInfoList);
    const [vehicleSelected, setVehicleSelected] = useState({}); //지도이동 체크를 위해서.
    const planDateMidNight = useMemo(() => baseData ? new Date(baseData.planDate + AppWord.HMS_TAIL) : null, [baseData]);
    const [openVehicleSelectBox, setOpenVehicleSelectBox] = useState(false);
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const [sendGuideSms, responseSendMsg] = useMutation( SEND_TRANS_PLAN_GUIDE_SMS, {
        onCompleted: (data, option) => onCompleteSendGuideSms(data, option),
        onError: (error) => addAlert({title: "안내 문자 전송 오류", message: [error.message]})
    });

    useEffect(() => {
        if(baseData) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;

                setInputSetVehicle({
                    data: {vehPlates: item.vehPlates},
                    changed: false,
                    error: {},
                    hasError: false
                });
                setStoresBound(item.bindStores && item.bindStores.length > 0);
            }
            else {
                inputSet.data = makeDefaultData(baseData);
                inputSet.data.hocha = hocha;
                inputSet.data.dockno = lastDockno || baseData.basicDockno;
                inputSet.data.shprReqTime = baseData.planDate + AppWord.HMS_TAIL;
                inputSet.data.dptrReqTime = baseData.planDate + AppWord.HMS_TAIL;
                inputSet.data.arrReqTime = baseData.planDate + AppWord.HMS_TAIL;
                inputSet.data.zipgiBool = false;

                editing = false;
                Object.assign(inputSet.data, baseData);
                setInputSetVehicle({...AppObject.emptyInputData});
                setStoresBound(false);
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
        else {
            setInput({...AppObject.emptyInputData});
            setInputSetVehicle({...AppObject.emptyInputData});
            setIsEdit(false);
        }
    }, [hocha, item, baseData, lastDockno]);

    useEffect(() => {
        if(item && input.data.comId && vehInfoListOfDcData.records[input.data.comId]) {
            setVehInfoList(vehInfoListOfDcData.records[input.data.comId]);
        }
        else setVehInfoList(EmptyVehInfoList);
    }, [vehInfoListOfDcData, input.data.comId, item]);

    useEffect(() => {
        let veh = {};
        if(inputSetVehicle.data.vehPlates) {
            const vehFound = getVehicleObject(vehInfoList, input.data.comId, inputSetVehicle.data.vehPlates);
            if(vehFound) veh = vehFound;
        }
        setVehicleSelected(veh);
    }, [inputSetVehicle, input, vehInfoList]);

    /* ################### GQL callback ################### */
    const onCompleteSendGuideSms = (data, option) => {
        if(data.transPlanSendGuideSms.ok) {
            enqueueSnackbar('안내 문자 전송 완료', {variant: 'success', autoHideDuration: 1500});
        }
        else addAlert({title:"안내 문자 전송 오류", message:[data.transPlanSendGuideSms.message]});
    };
    
    /* ################### Event Handlers ################### */
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onChangeSelect = (id, value) => {
        onChangeValue(id, value === AppWord.ChooseValue ? undefined : value, false);
    };

    // 접안시간은 그냥 이것 써도 됨.
    const onChangeTime24 = (id, value, error) => {
        if(!Boolean(value) || value.length !== 4) {
            onChangeValue(id, value, error);
            return;
        }
        const oldValue = input.data[id];
        const hh = value.substring(0, 2);
        const mm = value.substring(2, 4);
        if(oldValue) {
            const hhmm = oldValue.substring(11, 16).replace(/\D/, '');
            if(hhmm !== value) {
                onChangeValue(
                    id,
                    oldValue.substring(0, 11) + hh + ":" + mm + ':00',
                    error
                );
            }
        }
        else onChangeValue(id, baseData.planDate + ' ' + hh + ":" + mm + ':00', error);
    };

    // 출발요청시간
    const onChangeDptrReqTime24 = (id, value, error) => {
        const arriveInfo = baseData.arriveInfo;
        // 1. 출발요청시간
        const dptrReqTime = input.data.dptrReqTime ? new Date(input.data.dptrReqTime) : planDateMidNight;
        const dptrReqHms = ValueMisc.parseHourMinSec(value);
        dptrReqTime.setHours(dptrReqHms.hour, dptrReqHms.minute);

        // 출발요청기준값 copy planDateMidNight
        const startRequestedToBe = new Date(planDateMidNight);
        const startReqHms = ValueMisc.parseHourMinSec(arriveInfo.startReqTimeToday);
        startRequestedToBe.setHours(startReqHms.hour, startReqHms.minute);

        // 2. 도착요청시간을 만들어 가기.
        const arrReqTime = new Date(planDateMidNight);

        if(arriveInfo.startReqTimeToday && dptrReqTime >= startRequestedToBe) {
            arrReqTime.setDate(arrReqTime.getDate() + 1);

            if(IsValid.string(arriveInfo.arriveReqTimeNext,4,10)) {
                const hms = ValueMisc.parseHourMinSec(arriveInfo.arriveReqTimeNext);
                arrReqTime.setHours(hms.hour, hms.minute);
            }
            else if(IsValid.string(arriveInfo.firstShipTime,4,10)) {
                const hms = ValueMisc.parseHourMinSec(arriveInfo.firstShipTime);
                arrReqTime.setHours(hms.hour, hms.minute);
            }
            else {
                arrReqTime.setHours( dptrReqTime.getHours(), dptrReqTime.getMinutes() + 240 );
            }
        }
        else if(IsValid.string(arriveInfo.basicExpectRunTime, 4, 10)) {
            const hms = ValueMisc.parseHourMinSec(arriveInfo.basicExpectRunTime);
            arrReqTime.setHours(
                dptrReqTime.getHours(),
                dptrReqTime.getMinutes() + hms.hour * 60 + hms.minute
            )
        }
        else arrReqTime.setHours( dptrReqTime.getHours(), dptrReqTime.getMinutes() + 240 );

        // 날짜 조정.ValueMisc.isSameDate(arrReqTime, dptrReqTime)
        if(arrReqTime.getDate() === dptrReqTime.getDate()) {
            if(arrReqTime <= dptrReqTime) arrReqTime.setDate(dptrReqTime.getDate() + 1);
        }

        // 3. 접안시간 만들어 가기.
        const shprReqTime = new Date(dptrReqTime);
        shprReqTime.setMinutes(dptrReqTime.getMinutes() - 50);

        const newInput = InputUtil.reflectData(input, id, ValueMisc.getYmdHms(dptrReqTime), error, InputMap, isEdit);
        const newInput2 = InputUtil.reflectData(newInput, InputMap.shprReqTime.id, ValueMisc.getYmdHms(shprReqTime), false, InputMap, isEdit);
        const newInput3 = InputUtil.reflectData(newInput2, InputMap.arrReqTime.id, ValueMisc.getYmdHms(arrReqTime), false, InputMap, isEdit);

        setInput(newInput3);
    };

    // 도착요청시간
    const onChangeArrReqTime24 = (id, value, error) => {
        const arriveInfo = baseData.arriveInfo;
        // 일단 출발요청시간 확인.
        const dptrReqTime = input.data.dptrReqTime ? new Date(input.data.dptrReqTime) : planDateMidNight;

        // 출발요청기준값 copy planDateMidNight
        const startRequestedToBe = new Date(planDateMidNight);
        const startReqHms = ValueMisc.parseHourMinSec(arriveInfo.startReqTimeToday);
        startRequestedToBe.setHours(startReqHms.hour, startReqHms.minute);

        // 도착요청시간을 만들어 가기.
        const arrReqTime = new Date(planDateMidNight);
        const hms = ValueMisc.parseHourMinSec(value);
        arrReqTime.setHours(hms.hour, hms.minute);

        // 필요하면 날짜 조정: 출발하라는 시각이 출발요청시간기준보다 늦으면 도착할 시각(arrReqTime)은 다음날로 설정
        if(arriveInfo.startReqTimeToday && dptrReqTime >= startRequestedToBe) {
            arrReqTime.setDate(dptrReqTime.getDate() + 1);
        }
        else if(arrReqTime <= dptrReqTime) arrReqTime.setDate(dptrReqTime.getDate() + 1);

        onChangeValue(id, ValueMisc.getYmdHms(arrReqTime), error);
        // temporary
        // onChangeTime24(id, value, error);
    };
    
    const onVehicleSelected = (id, value) => {
        const newData = {...inputSetVehicle};
        if(newData.data.vehPlates !== value) {
            newData.data.vehPlates = value===AppWord.ChooseKorean ? undefined : value;
            if(item) {
                // 기존 차량과 비교해서 changed 여부를 결정.
                // 같으면 원래 차량으로 다시 돌아간 것이므로.
                const vehFound = getVehicleObject(vehInfoList, input.data.comId, value);
                newData.changed = item.vehPlates !== value || item.comId !== vehFound.comId;
            }
            else newData.changed = true;
            setInputSetVehicle(newData);
        }
    };

    const onClickSetVehicle = () => {
        if(item && item.planId && vehicleSelected.vehPlates && input.data.comId) {
            const param = {
                planId: item.planId,
                vehPlates: vehicleSelected.vehPlates,
                comId: input.data.comId,
            };
            onRequestSetTruck(param);
        }
    };

    const onClickCancelSetVehicle = () => {
        setInputSetVehicle({
            data: {vehPlates: item.vehPlates},
            changed: false,
            error: {},
            hasError: false
        });
    };

    const onClickOffTruck = () => {
        if(item && item.vehPlates) {
            const confirm =  window.confirm(`${item.hocha}호차의 ${item.vehPlates} 차량 배차를 취소하시겠습니까?`);

            if(confirm) {
                onRequestOffTruck(item.planId);
            }
        }
    };

    const onClickSendGuideSms = () => {
        if(item && item.vehPlates && item.driverMobile && item.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued) {
            const yes = window.confirm(`${item.hocha}호차의 ${item.vehPlates} 차량에 안내 문자를 전송합니다.\n계속하시겠습니까?`);

            if(yes) {
                sendGuideSms({variables: {planId: item.planId}});
            }
        }
    };

    const onClickSavePlan = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        // 왜 숫자로 변환되어 오는지 모르겠음. 강제로 문자열로 변환.
        if(param.dockno !== null && param.dockno !== undefined)
            param.dockno = `${param.dockno}`.trim();
        onRequestSave(param, isEdit);
    };

    const onClickRemovePlan = () => {
        if(item) {
            const confirm =  window.confirm(`${item.hocha}호차 운송요청를 삭제합니다.\n삭제 후 복구할 수 없습니다. 계속하시겠습니까?`);

            if(confirm) {
                onRequestRemove(item);
            }
        }
    };

    const onClickGoToMap = () => {
        // set mapRequestRepo data
        const mapData = {
            job: MapValue.Job.ShowTruck,
            info: {
                vehInfoSeq:vehicleSelected.vehInfoSeq,
                vehPlates: vehicleSelected.vehPlates,
                driverName: vehicleSelected.driverName,
                gpsTime: vehicleSelected.gpsTime,
                lat: vehicleSelected.lat,
                lon: vehicleSelected.lon,
                location: vehicleSelected.location,
                posSys: vehicleSelected.posSys,
                direction: vehicleSelected.direction,
                speed: vehicleSelected.speed,
            }
        };
        mapRequestRepo({data: mapData});
    };

    const canAdd = checkAuth(authTableTree.tree.pr_trans_plan_add);
    const canEdit = checkAuth(authTableTree.tree.pr_trans_plan_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_trans_plan_remove);
    const canSetVehicle = checkAuth(authTableTree.tree.pr_trans_plan_set_truck);
    const canRemoveVehicle = checkAuth(authTableTree.tree.pr_trans_plan_off_truck);
    const canSendSms = checkAuth(authTableTree.tree.pr_trans_plan_guide_sms);

    const renderBindStores = () => {
        const style= {
            padding:'0.4rem', display:'flex', alignItems:'center',
            overflow: 'hidden', textOverflow: 'ellipsis',
            backgroundColor: '#eee', borderRadius: '0.5rem',
        };
        // 
        if(storesBound && item && item.bindStores && item.bindStores.length > 0) {
            const stores = item.bindStores;
            return (
                <Box style={style}>
                    {
                        stores.map((store)=>{
                            return (
                                <span key={store.storeId}> ..{store.storeName}</span>
                            )
                        })
                    }
                </Box>
            );
        }
        return (
            <Box style={style}>
                <StorefrontIcon
                    fontSize={AppWord.SMALL} color={AppStyle.mui.disabled}
                    style={{marginRight: 5}}
                />
                {item ? '동적 점포 없음' : <span style={{color:'#ccc'}}>동적: *******</span>}
            </Box>
        )
    };

    if(hocha === undefined || hocha === 0) {
        return(
            <NoDataBox
                title="날짜와 점포를 선택한 후 조회 버튼을 클릭하세요"
            />
        );
    }

    return (
        <Box>
            <Grid2 container spacing={1.5}>
                <Grid2 size={6}>
                    <VzmSelector
                        dict={InputMap.transTypeCd}
                        value={input.data.transTypeCd || AppWord.ChooseValue}
                        onChange={onChangeSelect}
                        list={waveList}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                    />
                </Grid2>
                <Grid2 size={6}>
                    {
                        input.data.reqStateFlag===TopCode.ReqStateFlag.TsrIssued ?
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            fullWidth={true}
                            onClick={()=>onRequestShowTsr(item)}
                        >TSR 발행됨</Button>
                        :
                        input.data.reqStateFlag===TopCode.ReqStateFlag.TsrNotIssued ?
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            fullWidth={true}
                            disabled={true}
                        >TSR 미발행</Button>
                        :
                        <span>&nbsp;</span>
                    }
                </Grid2>
                <Grid2 size={6}>
                    <VzmSelector
                        dict={InputMap.vehTypeId}
                        value={input.data.vehTypeId || AppWord.ChooseValue}
                        onChange={onChangeSelect}
                        list={vehTypeList}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                    />
                </Grid2>
                <Grid2 size={6}>
                    <VzmSelector
                        dict={InputMap.comId}
                        value={input.data.comId || AppWord.ChooseValue}
                        onChange={onChangeSelect}
                        list={comInfoList}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                    />
                </Grid2>
                <Grid2 size={12} sx={{paddingLeft: '0.3rem'}}>
                    <Box style={{display:'flex', alignItems:'center'}}>
                        <GrowCol>
                            <Time24Picker
                                dict={InputMap.shprReqTime}
                                value={getHhmm(input.data.shprReqTime)}
                                onChange={onChangeTime24}
                                variant={AppStyle.mui.standard}
                            />
                        </GrowCol>
                        <GrowCol>
                            <Time24Picker
                                dict={InputMap.dptrReqTime}
                                value={getHhmm(input.data.dptrReqTime)}
                                onChange={onChangeDptrReqTime24}
                                variant={AppStyle.mui.standard}
                            />
                        </GrowCol>
                        <GrowCol>
                            <Time24Picker
                                dict={InputMap.arrReqTime}
                                value={getHhmm(input.data.arrReqTime)}
                                onChange={onChangeArrReqTime24}
                                variant={AppStyle.mui.standard}
                            />
                            <Chip
                                label={
                                    input.data.arrReqTime !== input.data.dptrReqTime
                                    &&
                                    ValueMisc.isSameDate(input.data.arrReqTime, input.data.dptrReqTime)
                                    ?
                                    '당일'
                                    :
                                    '익일'
                                }
                                size={AppWord.SMALL}
                                color={
                                    input.data.arrReqTime !== input.data.dptrReqTime
                                    &&
                                    ValueMisc.isSameDate(input.data.arrReqTime, input.data.dptrReqTime)
                                    ?
                                    AppStyle.mui.default : AppStyle.mui.success
                                }
                            />
                        </GrowCol>
                    </Box>
                </Grid2>
                <Grid2 size={6}>
                    <VzmTextField
                        dict={InputMap.dockno}
                        value={input.data.dockno}
                        onChange={onChangeValue}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                        inputProps={{style:{fontSize:'0.85rem'}}}
                        size={AppWord.SMALL}
                    />
                </Grid2>
                <Grid2 size={6}>
                    <VzmIntField
                        dict={InputMap.addCharge}
                        value={input.data.addCharge}
                        onChange={onChangeValue}
                        fullWidth={true}
                        variant={AppStyle.mui.outlined}
                        inputProps={{style:{fontSize:'0.85rem'}}}
                    />
                </Grid2>
                <Grid2 size={12}>
                    <VzmTextField
                        dict={InputMap.note}
                        value={input.data.note}
                        onChange={onChangeValue}
                        fullWidth={true}
                        variant={AppStyle.mui.standard}
                        inputProps={{style:{fontSize:'0.85rem'}}}
                        size={AppWord.SMALL}
                    />
                </Grid2>
                <Grid2 size={6}>
                    {
                        item
                        ?
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            size={AppWord.SMALL}
                            style={{flexGrow:1, marginRight:2}}
                            startIcon={<CardGiftcardIcon />}
                            disabled={!input.changed || holdMutation || !canEdit}
                            fullWidth={true}
                            onClick={onClickSavePlan}
                        >
                            수정
                        </Button>
                        :
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            size={AppWord.SMALL}
                            style={{flexGrow:1, marginRight:2}}
                            startIcon={<CardGiftcardIcon />}
                            disabled={!input.changed || holdMutation || !canAdd}
                            fullWidth={true}
                            onClick={onClickSavePlan}
                        >
                            추가
                        </Button>
                    }
                </Grid2>
                <Grid2 size={6}>
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.warning}
                        fullWidth={true}
                        size={AppWord.SMALL}
                        startIcon={<ClearIcon />}
                        disabled={!Boolean(item) || holdMutation || !canRemove}
                        onClick={onClickRemovePlan}
                    >
                        삭제
                    </Button>
                </Grid2>
                <Grid2 size={12}
                    style={{backgroundColor:'#ccf2ff', borderRadius:'0.5rem', }}
                >
                    <Box style={{display:'flex', alignItems:'center'}}>
                        <LabelBox
                        >
                            <Button
                                style={{
                                    backgroundColor:'#eef9ff', color: AppStyle.PrimaryRGB
                                }}
                                endIcon={<SearchIcon />}
                                onClick={()=>setOpenVehicleSelectBox(true)}
                            >
                                {item && inputSetVehicle.data.vehPlates && vehInfoList.length > 1
                                    ? inputSetVehicle.data.vehPlates
                                    : AppWord.ChooseKorean
                                }
                            </Button>
                        </LabelBox>
                        <LabelBox>
                            <PersonIcon fontSize={AppWord.SMALL} color={AppStyle.mui.info} />
                            {
                                vehicleSelected.driverName ||
                                input.data.driverName ?
                                <span>
                                    {
                                        vehicleSelected.driverName || input.data.driverName
                                    }
                                </span>
                                :
                                <span style={{color:'#ccc'}}>[없음]</span>
                            }
                        </LabelBox>
                        <LabelBox>
                            <SystemSecurityUpdateGoodIcon fontSize={AppWord.SMALL} color={AppStyle.mui.info} />
                            {
                                vehicleSelected.driverMobile ||
                                input.data.driverMobile ?
                                <span>{vehicleSelected.driverMobile || input.data.driverMobile}</span>
                                :
                                <span style={{color:'#ccc'}}>[없음]</span>
                            }
                        </LabelBox>
                    </Box>
                    <Box style={{
                        display:'flex', alignItems:'center', justifyContent:'center',
                        paddingBottom: '0.8rem'}}
                    >
                        {
                            inputSetVehicle.data.vehPlates && item && !inputSetVehicle.changed
                            ?
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.warning}
                                startIcon={<CancelScheduleSendIcon />}
                                style={{minWidth: '30%'}}
                                disabled={!canRemoveVehicle || holdMutation}
                                size={AppWord.SMALL}
                                onClick={onClickOffTruck}
                            >초기화</Button>
                            :
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                startIcon={<LocalShippingIcon />}
                                style={{minWidth: '30%'}}
                                disabled={!inputSetVehicle.changed || !item || !canSetVehicle || holdMutation}
                                size={AppWord.SMALL}
                                onClick={onClickSetVehicle}
                            >
                                등록
                            </Button>
                        }
                        {
                            inputSetVehicle.data.vehPlates && inputSetVehicle.changed
                            ?
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.warning}
                                startIcon={<ClearIcon />}
                                style={{minWidth: '30%', marginLeft: '0.5rem'}}
                                size={AppWord.SMALL}
                                onClick={onClickCancelSetVehicle}
                            >등록취소</Button>
                            :
                            <Button
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.inherit}
                                startIcon={<LocationOnIcon />}
                                style={{minWidth: '30%', marginLeft: '0.5rem'}}
                                disabled={vehicleSelected.vehPlates && vehicleSelected.gpsTime ? false : true}
                                size={AppWord.SMALL}
                                onClick={onClickGoToMap}
                            >
                                위치
                            </Button>}
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.inherit}
                            startIcon={<MessageIcon />}
                            style={{minWidth: '30%', marginLeft: '0.5rem'}}
                            disabled={
                                !Boolean(item) || !canSendSms || inputSetVehicle.changed
                                || !item.vehPlates || !item.driverMobile || item.reqStateFlag===TopCode.ReqStateFlag.TsrIssued
                                || responseSendMsg.loading || holdMutation}
                            size={AppWord.SMALL}
                            onClick={onClickSendGuideSms}
                        >
                            메시지
                        </Button>
                    </Box>
                </Grid2>
                <Grid2 size={12}>
                    {renderBindStores()}
                </Grid2>
            </Grid2>
            <VehInfoSelectDialog
                open={openVehicleSelectBox}
                onClose={()=>setOpenVehicleSelectBox(false)}
                records={vehInfoList.slice(1)}
                comInfoList={comInfoList}
                onSelect={(veh) => {
                    onVehicleSelected(TransPlanLaunchDictionary.inputMapToSetVeh.vehPlates.id, veh.vehPlates);
                    setOpenVehicleSelectBox(false);
                }}
                defaultSearchText={inputSetVehicle.data.vehPlates}
            />
        </Box>
    );
}

/*

            {hocha}호차, dc={baseData.dcCd}, arriveId={baseData.arriveId}, storeId={baseData.storeId}, planDate={baseData.planDate}<br/>
            input.data.shprReqTime={item ? item.shprReqTime : 'null'}
*/
